// 영업·상담 (매장 · 방문 일정 · 질문지 · 문서함)
//
// store/index.js 가 이 조각들을 펼쳐 하나의 루트 스토어로 합친다.
// Vuex 의 modules 옵션이 아니라 "조각"인 이유: Vuex 는 namespaced 여부와 무관하게
// 모듈 state 를 모듈 이름 아래에 중첩시킨다. 진짜 모듈로 바꾸면 store.state.tasks 가
// store.state.tasks.tasks 가 되어 소비자 40곳을 전부 고쳐야 한다.
// 조각 합성은 동작이 100% 동일하면서 파일만 나눈다.
//
// 매장 상세 · 방문 상세는 state 에 두지 않고 액션이 돌려준 값을 화면이 들고 있는다.
// 한 번에 한 화면만 보는 데이터라 전역에 두면 이전 매장 값이 잠깐 비쳐 보이기만 한다.
// 목록(매장 · 질문지 · 문서함)만 state 에 둔다 — 여러 화면이 같이 읽는다.

import http from '@/utils/http'

const fail = (commit, error, fallback) => {
  commit('SET_ERROR', error.response?.data?.message || fallback)
  throw error
}

export const state = {
  salesStores: [],
  salesQuestionSets: [],
  salesDocuments: [],
  // IRUDAONE 문서함과 마지막으로 맞춘 결과 { enabled, ok, at, error }
  salesDocsSync: { enabled: false, ok: null, at: null }
}

export const mutations = {
  SET_SALES_STORES(state, stores) {
    state.salesStores = stores
  },

  /** 상세 화면에서 고친 매장을 목록에도 반영한다. 목록 전용 집계 필드는 보존한다. */
  MERGE_SALES_STORE(state, store) {
    const i = state.salesStores.findIndex((s) => s.id === store.id)
    if (i === -1) state.salesStores.unshift(store)
    else state.salesStores.splice(i, 1, { ...state.salesStores[i], ...store })
  },

  REMOVE_SALES_STORE(state, id) {
    state.salesStores = state.salesStores.filter((s) => s.id !== id)
  },

  SET_SALES_QUESTION_SETS(state, sets) {
    state.salesQuestionSets = sets
  },

  SET_SALES_DOCUMENTS(state, docs) {
    state.salesDocuments = docs
  },

  SET_SALES_DOCS_SYNC(state, sync) {
    state.salesDocsSync = { ...state.salesDocsSync, ...(sync || {}) }
  }
}

export const actions = {
  // ---------- 매장 ----------
  async fetchSalesStores({ commit }) {
    try {
      const { data } = await http.get('/api/sales/stores')
      commit('SET_SALES_STORES', data.stores || [])
      return data.stores || []
    } catch (error) {
      return fail(commit, error, '매장 목록을 불러오지 못했습니다.')
    }
  },

  async createSalesStore({ commit }, payload) {
    try {
      const { data } = await http.post('/api/sales/stores', payload)
      commit('MERGE_SALES_STORE', data.store)
      return data.store
    } catch (error) {
      return fail(commit, error, '매장을 등록하지 못했습니다.')
    }
  },

  /** 상세: { store, visits, logs, checklist, files, answers } */
  async fetchSalesStore({ commit }, id) {
    try {
      const { data } = await http.get(`/api/sales/stores/${id}`)
      return data
    } catch (error) {
      return fail(commit, error, '매장을 불러오지 못했습니다.')
    }
  },

  /**
   * optimistic: 서버 응답을 기다리지 않고 목록부터 고친다(보드에서 단계 옮기기).
   * 응답을 기다리면 놓은 카드가 원래 열로 돌아갔다가 한참 뒤에 옮겨 간다.
   * 실패하면 고친 필드만 원래 값으로 되돌린다 — 서버는 아무것도 바꾸지 않았다.
   */
  async updateSalesStore({ commit, state }, { id, patch, optimistic = false }) {
    const prev = optimistic ? state.salesStores.find((s) => s.id === id) : null
    const before = prev ? Object.fromEntries(Object.keys(patch).map((k) => [k, prev[k]])) : null
    if (prev) commit('MERGE_SALES_STORE', { id, ...patch })
    try {
      const { data } = await http.patch(`/api/sales/stores/${id}`, patch)
      commit('MERGE_SALES_STORE', data.store)
      return data.store
    } catch (error) {
      if (before) commit('MERGE_SALES_STORE', { id, ...before })
      return fail(commit, error, '매장 정보를 저장하지 못했습니다.')
    }
  },

  /** 운영 계약 종료 처리 / 다시 운영 중으로. action: 'end' | 'reopen' */
  async setSalesContractState({ commit }, { id, action }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${id}/contract/${action === 'end' ? 'end' : 'reopen'}`)
      commit('MERGE_SALES_STORE', data.store)
      return data.store
    } catch (error) {
      return fail(commit, error, '계약 상태를 바꾸지 못했습니다.')
    }
  },

  async deleteSalesStore({ commit }, id) {
    try {
      await http.delete(`/api/sales/stores/${id}`)
      commit('REMOVE_SALES_STORE', id)
    } catch (error) {
      return fail(commit, error, '매장을 지우지 못했습니다.')
    }
  },

  // ---------- 상담 기록 ----------
  async addSalesLog({ commit }, { storeId, payload }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${storeId}/logs`, payload)
      return data
    } catch (error) {
      return fail(commit, error, '기록을 남기지 못했습니다.')
    }
  },

  async deleteSalesLog({ commit }, logId) {
    try {
      await http.delete(`/api/sales/logs/${logId}`)
    } catch (error) {
      return fail(commit, error, '기록을 지우지 못했습니다.')
    }
  },

  // ---------- 체크리스트 ----------
  /** dedupe: 이미 같은 문구가 있으면 건너뛴다 (단계 기본 할 일 넣기) */
  async addSalesChecklist({ commit }, { storeId, items, dedupe = false }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${storeId}/checklist`, { items, dedupe })
      return data.items || []
    } catch (error) {
      return fail(commit, error, '할 일을 추가하지 못했습니다.')
    }
  },

  async updateSalesChecklist({ commit }, { id, patch }) {
    try {
      const { data } = await http.patch(`/api/sales/checklist/${id}`, patch)
      return data.item
    } catch (error) {
      return fail(commit, error, '할 일을 저장하지 못했습니다.')
    }
  },

  /** 서류 메일 발송 설정 — { enabled, from, fromName, replyTo }. 화면이 버튼을 켤지 정한다 */
  async fetchSalesMailInfo() {
    const { data } = await http.get('/api/sales/mail-info')
    return data
  },

  /** 서류 메일 발송 → { sent, rejected, files, checklistDone }. 매장 '전달' 기록은 서버가 남긴다 */
  async sendSalesEmail({ commit }, { storeId, ...payload }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${storeId}/email`, payload)
      return data
    } catch (error) {
      return fail(commit, error, '메일을 보내지 못했습니다.')
    }
  },

  async deleteSalesChecklist({ commit }, id) {
    try {
      await http.delete(`/api/sales/checklist/${id}`)
    } catch (error) {
      return fail(commit, error, '할 일을 지우지 못했습니다.')
    }
  },

  // ---------- 질문지 답 ----------
  // a: 메모 · 서술, v: 선택값(복수면 배열), rows: 표 — 넘긴 칸만 바꾼다
  async saveSalesAnswer({ commit }, { storeId, setId, itemId, a, v, rows, ok }) {
    try {
      const body = { itemId }
      if (a !== undefined) body.a = a
      if (v !== undefined) body.v = v
      if (rows !== undefined) body.rows = rows
      if (ok !== undefined) body.ok = ok
      const { data } = await http.put(`/api/sales/stores/${storeId}/answers/${setId}`, body)
      return data.answer
    } catch (error) {
      return fail(commit, error, '답을 저장하지 못했습니다.')
    }
  },

  // ---------- 방문 일정 ----------
  async fetchSalesVisits({ commit }, params = {}) {
    try {
      const { data } = await http.get('/api/sales/visits', { params })
      return data.visits || []
    } catch (error) {
      return fail(commit, error, '일정을 불러오지 못했습니다.')
    }
  },

  /** 참석자 일정 겹침: { date, start, end, userIds:[], excludeVisitId } → [{ userId, eventType, startDate, endDate, isAllDay }] */
  async fetchSalesVisitConflicts(_, { date, start, end, userIds, excludeVisitId }) {
    if (!date || !userIds?.length) return []
    const { data } = await http.get('/api/sales/visit-conflicts', {
      params: { date, start: start || undefined, end: end || undefined, userIds: userIds.join(','), excludeVisitId: excludeVisitId || undefined }
    })
    return data.conflicts || []
  },

  async createSalesVisit({ commit }, { storeId, payload }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${storeId}/visits`, payload)
      return data.visit
    } catch (error) {
      return fail(commit, error, '일정을 만들지 못했습니다.')
    }
  },

  /** 상세: { visit, photos } */
  async fetchSalesVisit({ commit }, id) {
    try {
      const { data } = await http.get(`/api/sales/visits/${id}`)
      return data
    } catch (error) {
      return fail(commit, error, '일정을 불러오지 못했습니다.')
    }
  },

  async updateSalesVisit({ commit }, { id, patch }) {
    try {
      const { data } = await http.patch(`/api/sales/visits/${id}`, patch)
      return data.visit
    } catch (error) {
      return fail(commit, error, '일정을 저장하지 못했습니다.')
    }
  },

  async deleteSalesVisit({ commit }, id) {
    try {
      await http.delete(`/api/sales/visits/${id}`)
    } catch (error) {
      return fail(commit, error, '일정을 지우지 못했습니다.')
    }
  },

  // ---------- 서류 · 사진 ----------
  /** formData: file(선택) · category · title · link · documentId · sentAt · channel · note · visitId */
  async uploadSalesFile({ commit }, { storeId, formData }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${storeId}/files`, formData)
      // checklistDone: 이 전달로 끝난 할 일 수 — 화면이 할 일 목록을 다시 읽을지 정한다
      return { ...data.file, checklistDone: data.checklistDone || 0 }
    } catch (error) {
      return fail(commit, error, '서류를 올리지 못했습니다.')
    }
  },

  /** 현장 사진 구역만 바꾼다 → { id, area } */
  async setSalesPhotoArea({ commit }, { id, area }) {
    try {
      const { data } = await http.patch(`/api/sales/files/${id}`, { area })
      return data.file
    } catch (error) {
      return fail(commit, error, '사진 구역을 바꾸지 못했습니다.')
    }
  },

  async deleteSalesFile({ commit }, id) {
    try {
      await http.delete(`/api/sales/files/${id}`)
    } catch (error) {
      return fail(commit, error, '파일을 지우지 못했습니다.')
    }
  },

  // ---------- 변경 기록 ----------
  /** params: { storeId, actorId, target, before, limit } → { activities, more } */
  async fetchSalesActivities({ commit }, params = {}) {
    try {
      const { data } = await http.get('/api/sales/activities', { params })
      return { activities: data.activities || [], more: !!data.more }
    } catch (error) {
      return fail(commit, error, '변경 기록을 불러오지 못했습니다.')
    }
  },

  // ---------- 도입 체크리스트 링크 ----------
  async fetchSalesOnboarding({ commit }, storeId) {
    try {
      const { data } = await http.get(`/api/sales/stores/${storeId}/onboarding`)
      return data.links || []
    } catch (error) {
      return fail(commit, error, '도입 체크리스트 링크를 불러오지 못했습니다.')
    }
  },

  async createSalesOnboarding({ commit }, { storeId, days }) {
    try {
      const { data } = await http.post(`/api/sales/stores/${storeId}/onboarding`, { days })
      return data
    } catch (error) {
      return fail(commit, error, '링크를 만들지 못했습니다.')
    }
  },

  /** action: revoke · reopen · extend */
  async updateSalesOnboarding({ commit }, { linkId, action }) {
    try {
      const { data } = await http.patch(`/api/sales/onboarding/${linkId}`, { action })
      return data.link
    } catch (error) {
      return fail(commit, error, '링크를 고치지 못했습니다.')
    }
  },

  async applySalesOnboarding({ commit }, { linkId, done = false }) {
    try {
      const { data } = await http.post(`/api/sales/onboarding/${linkId}/apply`, { done })
      return data
    } catch (error) {
      return fail(commit, error, '질문지에 옮겨 적지 못했습니다.')
    }
  },

  // ---------- 질문지 템플릿 ----------
  async fetchSalesQuestionSets({ commit }, { all = false } = {}) {
    try {
      const { data } = await http.get('/api/sales/question-sets', { params: all ? { all: 1 } : {} })
      if (!all) commit('SET_SALES_QUESTION_SETS', data.sets || [])
      return data.sets || []
    } catch (error) {
      return fail(commit, error, '질문지를 불러오지 못했습니다.')
    }
  },

  async saveSalesQuestionSet({ commit, dispatch }, { id, payload }) {
    try {
      const { data } = id
        ? await http.put(`/api/sales/question-sets/${id}`, payload)
        : await http.post('/api/sales/question-sets', payload)
      dispatch('fetchSalesQuestionSets').catch(() => {})
      return data.set
    } catch (error) {
      return fail(commit, error, '질문지를 저장하지 못했습니다.')
    }
  },

  // ---------- 문서함 ----------
  /** force: 문서함과 바로 맞춘다([지금 맞추기]). 평소엔 서버가 1분에 한 번만 맞춘다 */
  async fetchSalesDocuments({ commit }, { force = false } = {}) {
    try {
      const { data } = await http.get('/api/sales/documents', { params: force ? { sync: 1 } : {} })
      commit('SET_SALES_DOCUMENTS', data.documents || [])
      if (data.sync) commit('SET_SALES_DOCS_SYNC', data.sync)
      return data.documents || []
    } catch (error) {
      return fail(commit, error, '문서함을 불러오지 못했습니다.')
    }
  },

  async saveSalesDocument({ commit, dispatch }, { id, payload }) {
    try {
      const { data } = id
        ? await http.put(`/api/sales/documents/${id}`, payload)
        : await http.post('/api/sales/documents', payload)
      await dispatch('fetchSalesDocuments')
      return data.document
    } catch (error) {
      return fail(commit, error, '문서를 저장하지 못했습니다.')
    }
  },

  async deleteSalesDocument({ commit, dispatch }, id) {
    try {
      await http.delete(`/api/sales/documents/${id}`)
      await dispatch('fetchSalesDocuments')
    } catch (error) {
      return fail(commit, error, '문서를 지우지 못했습니다.')
    }
  }
}
